import React from "react";
import { useTranslation } from "react-i18next";
import {
  HiOutlineLocationMarker,
  HiOutlineTruck,
  HiOutlineClock,
  HiCheck,
} from "react-icons/hi";
import { Timestamp } from "../../types";
import { formatAddressOneLine } from "../../utils/address";
import { formatRouteTimelineTiming } from "../../helpers/routeMappers";

// Loose, display-only shapes for the timeline. Intentionally permissive
// (nullable fields, `any` timestamps, string status) because callers pass
// partially-populated route/cargo data straight from Firestore/API responses.
// Kept distinct from the canonical ILocation/IRouteItem/ICargo domain types.
interface TimelineLocation {
  addressLine?: string | null;
  displayName?: string | null;
  city?: string | null;
  postCode?: string | null;
}

interface TimelineStop {
  stopNumber: number;
  isPickup?: boolean;
  location?: TimelineLocation | null;
  stopDate?: any;
  dayOffset?: number;
  status?: string;
  completedAt?: any;
  signatureUrl?: string | null;
  timing?: {
    after?: string;
    before?: string;
    earliest?: boolean;
    latest?: boolean;
  };
}

interface TimelineCargo {
  quantity: number;
  descriptionShort?: string;
  weight?: number;
  length?: number;
  width?: number;
  height?: number;
  pickedUpAt?: any; // Timestamp when cargo was picked up
  deliveredAt?: any; // Timestamp when cargo was delivered
  itinerary?: {
    pickupStopNumber?: number;
    deliveryStopNumber?: number;
  };
}

interface RouteTimelineProps {
  stops: TimelineStop[];
  cargo?: TimelineCargo[];
  variant?: "order" | "template";
}

export const RouteTimeline: React.FC<RouteTimelineProps> = ({
  stops,
  cargo,
  variant = "order",
}) => {
  const { t } = useTranslation(["common", "orders"]);

  const isTemplate = variant === "template";

  return (
    <div className="relative">
      {stops.map((stop, index) => {
        const isPickup = stop.isPickup ?? stop.stopNumber === 0;
        const isLast = index === stops.length - 1;

        // Find cargo items for this stop to get completion time
        const cargoItems =
          cargo?.filter((item) => {
            if (isPickup) {
              return item.itinerary?.pickupStopNumber === stop.stopNumber;
            } else {
              return item.itinerary?.deliveryStopNumber === stop.stopNumber;
            }
          }) || [];

        // Get completion time for display
        const completionTime = !isTemplate
          ? cargoItems.find((item) => {
              if (isPickup) {
                return (
                  item.pickedUpAt !== null && item.pickedUpAt !== undefined
                );
              } else {
                return (
                  item.deliveredAt !== null && item.deliveredAt !== undefined
                );
              }
            })
          : undefined;

        // Determine stop status with smart "next stop" logic
        const isCompleted =
          !isTemplate &&
          (stop.completedAt != null || stop.status === "Completed");
        const isSigned = !isTemplate && !!stop.signatureUrl;

        // Find if this is the next stop in the route (first non-completed stop after completed ones)
        const isNextStop =
          !isTemplate &&
          !isCompleted &&
          stops
            .slice(0, index)
            .every(
              (prevStop) =>
                prevStop.completedAt != null || prevStop.status === "Completed",
            );

        // Also check if this is the first stop and no stops are completed yet (start of route)
        const isFirstUncompletedStop =
          !isTemplate &&
          !isCompleted &&
          index === 0 &&
          stops.every(
            (s) =>
              (s.completedAt === null || s.completedAt === undefined) &&
              s.status !== "Completed",
          );

        // Determine if in progress - either explicitly set, next logical stop, or first stop
        const isInProgress =
          !isTemplate &&
          (stop.status === "InProgress" ||
            isNextStop ||
            isFirstUncompletedStop);

        return (
          <div key={index} className={`relative ${!isLast ? "mb-4" : ""}`}>
            {/* Connecting Line */}
            {!isLast && (
              <div className="absolute left-[20px] top-10 bottom-[-32px] w-1">
                <div
                  className={`w-full h-full ${
                    isCompleted
                      ? "bg-gradient-to-b from-lgb-green-200 to-lgb-green-100 dark:from-lgb-green-800/40 dark:to-lgb-green-900/20"
                      : isInProgress
                        ? "bg-gradient-to-b from-lgb-blue-200 to-lgb-blue-100 dark:from-lgb-blue-800/40 dark:to-lgb-blue-900/20"
                        : "bg-gradient-to-b from-gray-200 to-gray-100 dark:from-gray-700/40 dark:to-gray-800/20"
                  }`}
                />
              </div>
            )}

            <div className="flex items-start">
              <div className="relative">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center shadow-sm ${
                    isTemplate
                      ? "bg-surface border border-solid border-border"
                      : isCompleted
                        ? "bg-success-muted border-2 border-solid border-lgb-green-200 dark:bg-lgb-green-900/30 dark:border-lgb-green-400/30"
                        : isInProgress && (isNextStop || isFirstUncompletedStop)
                          ? "bg-brand-muted border border-solid border-brand-muted dark:bg-gray-800 dark:border-lgb-blue-400"
                          : isInProgress
                            ? "bg-gray-50 border border-solid border-gray-300 dark:bg-lgb-blue-900/30 dark:border-gray-600"
                            : "bg-gray-50 border border-dashed border-gray-300 dark:bg-gray-800 dark:border-gray-600"
                  }`}
                >
                  {isCompleted ? (
                    <HiCheck className="w-5 h-5 text-lgb-green-600 dark:text-success" />
                  ) : isPickup ? (
                    <HiOutlineLocationMarker
                      className={`w-5 h-5 ${
                        isInProgress || isNextStop || isFirstUncompletedStop
                          ? "text-brand dark:text-brand"
                          : "text-muted"
                      }`}
                    />
                  ) : (
                    <HiOutlineTruck
                      className={`w-5 h-5 ${
                        isInProgress || isNextStop || isFirstUncompletedStop
                          ? "text-brand dark:text-brand"
                          : isCompleted
                            ? "text-lgb-green-600 dark:text-success"
                            : "text-muted"
                      }`}
                    />
                  )}
                </div>
              </div>

              <div className="flex-1 ml-4">
                <div
                  className={`rounded-lg py-3 px-3 shadow-sm ${
                    isTemplate
                      ? "bg-surface border border-solid border-border"
                      : isCompleted
                        ? "bg-success-muted border border-solid border-lgb-green-200 dark:bg-lgb-green-400/10 dark:border-lgb-green-400/30"
                        : isInProgress
                          ? isNextStop || isFirstUncompletedStop
                            ? "bg-white border border-solid border-border-strong dark:bg-gray-800 dark:border-lgb-grey-400"
                            : "bg-white border border-solid border-gray-100 dark:bg-gray-800 dark:border-gray-700"
                          : "bg-gray-50 border border-dashed border-gray-300 dark:bg-gray-800 dark:border-gray-600"
                  }`}
                >
                  {/* Address - Main focus */}
                  <div className="mb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <div className="text-base font-semibold text-text">
                            {/* Address line - inline on desktop, stacked on mobile */}
                            <span>
                              {stop.location?.addressLine ||
                                t("common:no_address")}
                            </span>
                            {stop.location?.postCode && stop.location?.city && (
                              <>
                                <span className="hidden sm:inline text-sm font-normal text-gray-600 dark:text-gray-300">
                                  , {stop.location.postCode}{" "}
                                  {stop.location.city}
                                </span>
                                <div className="block sm:hidden text-sm font-normal text-gray-600 dark:text-gray-300">
                                  {stop.location.postCode} {stop.location.city}
                                </div>
                              </>
                            )}
                          </div>

                          <div className="flex items-center gap-4">
                            {isSigned && (
                              <span className="text-xs font-medium text-secondary">
                                ✍️{" "}
                                {t(
                                  "common:signature_received",
                                  "Signatur mottatt",
                                )}
                              </span>
                            )}
                            {isInProgress &&
                              !isCompleted &&
                              (isNextStop || isFirstUncompletedStop) && (
                                <span className="text-xs font-medium text-brand">
                                  🚚 {t("common:next_stop", "Neste stopp")}
                                </span>
                              )}
                          </div>
                        </div>
                        {/* Display name directly under address line */}
                        {stop.location?.displayName && (
                          <div className="mt-1 text-xs font-normal text-muted">
                            {stop.location.displayName}
                          </div>
                        )}
                      </div>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ml-3 ${
                          isPickup
                            ? "bg-pickup-muted text-pickup"
                            : "bg-delivery-muted text-delivery"
                        }`}
                      >
                        {isPickup
                          ? completionTime?.pickedUpAt
                            ? `${t("common:picked_up")} ${Timestamp.toDisplayDate(completionTime.pickedUpAt)} ${Timestamp.ToDisplayTime(completionTime.pickedUpAt)}`
                            : t("common:pickup")
                          : completionTime?.deliveredAt
                            ? `${t("common:delivered")} ${Timestamp.toDisplayDate(completionTime.deliveredAt)} ${Timestamp.ToDisplayTime(completionTime.deliveredAt)}`
                            : t("common:delivery")}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {/* Time indicator */}
                      {(isTemplate || stop.stopDate || stop.timing) && (
                        <div className="text-sm text-gray-600 dark:text-gray-300 flex items-center">
                          <HiOutlineClock className="w-3.5 h-3.5 mr-1" />
                          {isTemplate
                            ? [
                                t(
                                  (stop.dayOffset ?? 0) === 0
                                    ? "orders:create.route_selection.same_day"
                                    : "orders:create.route_selection.plus_days",
                                  { count: stop.dayOffset ?? 0 },
                                ),
                                formatRouteTimelineTiming(
                                  { timing: stop.timing },
                                  t,
                                ),
                              ]
                                .filter(Boolean)
                                .join(" · ")
                            : formatRouteTimelineTiming(stop, t)}
                        </div>
                      )}
                      {/* Status indicator - only show other in-progress statuses */}
                      {isInProgress &&
                        !isCompleted &&
                        !(isNextStop || isFirstUncompletedStop) && (
                          <span className="text-xs font-medium text-lgb-blue-600 dark:text-brand">
                            ⏳ {t("common:in_progress", "Pågår")}
                          </span>
                        )}
                    </div>
                    {stop.location?.addressLine &&
                      stop.location?.city &&
                      stop.location?.postCode && (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                            formatAddressOneLine(stop.location),
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand hover:text-brand dark:text-brand dark:hover:text-brand text-sm inline-flex items-center whitespace-nowrap"
                        >
                          <HiOutlineLocationMarker className="w-4 h-4 mr-1" />
                          Google Maps
                        </a>
                      )}
                  </div>

                  {/* Cargo Items */}
                  {cargoItems.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                      <div className="text-sm font-medium text-muted mb-2">
                        {t("common:cargo")}:
                      </div>
                      <div className="space-y-2">
                        {cargoItems.map((item, itemIndex) => (
                          <div key={itemIndex}>
                            <div className="flex items-center text-text">
                              <span className="text-sm font-medium">
                                {item.quantity}x
                              </span>
                              <span className="ml-1 text-sm">
                                {item.descriptionShort ||
                                  t("common:unnamed_product")}
                              </span>
                            </div>
                            {/* Only show dimensions/weight if any of them are greater than 0 */}
                            {((item.weight ?? 0) > 0 ||
                              (item.length ?? 0) > 0 ||
                              (item.width ?? 0) > 0 ||
                              (item.height ?? 0) > 0) && (
                              <div className="text-sm text-muted">
                                {(item.weight ?? 0) > 0 && `${item.weight}kg`}
                                {(item.length ?? 0) > 0 &&
                                  (item.width ?? 0) > 0 &&
                                  (item.height ?? 0) > 0 && (
                                    <>
                                      {(item.weight ?? 0) > 0 && " • "}
                                      {`${item.length}×${item.width}×${item.height}cm`}
                                    </>
                                  )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RouteTimeline;
